import { chatApi, commonApi, fetchSetting } from "../services/api";
import { Bubble, Prompts, Sender, useXAgent, useXChat } from "@ant-design/x";
import {
  GetProp,
  Typography,
  Upload,
  Modal,
  message as antMessage,
} from "antd/lib";
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useIntl } from "react-intl";
import aiAvatar from "../assets/ai-avatar.png";
import userAvatar from "../assets/user-avatar.png";
import {
  UserOutlined,
  PictureOutlined,
  LoadingOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useStyle } from "../styles/IndependentChat.styles";
import { renderLocalMessage, RatingButtons } from "./ChatUtils";
import { Flex } from "antd";
import { Space } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import { SmartCompressor } from "../utils/compressors/SmartCompressor";
import DraggableCloseButton from "./DraggableCloseButton";
import QuickQuestionFloating from "./QuickQuestionFloating";
import ChatEvaluation from "./ChatEvaluation";

// 移除本地 RequestFnInfo 定义以避免与库类型冲突

// 新增聊天状态枚举
type ChatStatus =
  | "active" // 正常聊天中
  | "closed-no-eval" // 已关闭，无需评价
  | "closed-need-eval" // 已关闭，需要评价
  | "closed-evaluated"; // 已关闭，已完成评价

// 评价数据结构
interface EvaluationData {
  rating: number; // 1-5星评分
  feedback?: string; // 可选文字反馈
  timestamp: number; // 评价时间
}

const senderPromptsItems: GetProp<typeof Prompts, "items"> = [];

interface IndependentProps {
  token: string;
  code: string;
  onRef?: (ref: { fetchChatContent: () => Promise<void> }) => void;
}

interface ChatMessage {
  key: string;
  role: string;
  content: React.ReactNode;
  typing?: boolean;
  footer?: React.ReactNode;
  image?: string;
  video?: string;
  rating?: number; // 评分状态：1=点赞，2=点踩，0=未评分
  uuid?: string; // 记忆ID，用于评分
  messageType?: "text" | "image" | "video" | "evaluation"; // 消息类型
  evaluationData?: EvaluationData; // 评价数据
}

const IndependentChat: React.FC<IndependentProps> = ({
  token,
  code,
  onRef,
}) => {
  // ==================== Style ====================
  const { styles } = useStyle();
  const intl = useIntl();

  // ==================== State ====================
  const [content, setContent] = useState(""); // 输入框内容
  const [messages, setMessages] = useState<
    GetProp<typeof Bubble.List, "items">
  >([]); // 消息列表
  const [previewVisible, setPreviewVisible] = useState(false); // 图片预览模态框
  const [previewImage, setPreviewImage] = useState(""); // 图片预览
  const tokenRef = useRef(token); // 令牌
  const [imageLoading, setImageLoading] = useState(false); // 图片加载状态
  const [previewBase64, setPreviewBase64] = useState<string | null>(null); // 图片预览Base64
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null); // 上传图片URL
  const uploadedImageUrlRef = useRef<string | null>(null); // 上传图片URL引用
  const [videoLoading, setVideoLoading] = useState(false); // 视频加载状态
  const [previewVideo, setPreviewVideo] = useState<string | null>(null); // 视频预览
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null); // 上传视频URL
  const uploadedVideoUrlRef = useRef<string | null>(null); // 上传视频URL引用
  const [videoVisible, setVideoVisible] = useState(false); // 视频预览模态框
  const [currentVideo, setCurrentVideo] = useState(""); // 当前视频
  const [chatStatus, setChatStatus] = useState<ChatStatus>("active"); // 聊天状态
  const [messageRatings, setMessageRatings] = useState<Record<string, number>>(
    {}
  ); // 消息评分状态
  const fileInputRef = useRef<HTMLInputElement>(null); // 文件输入引用
  const [quickQuestions, setQuickQuestions] = useState<Bot.QuickQuestion[]>([]); // 快捷问题列表状态
  const [robotSettings, setRobotSettings] = useState<{
    robotCode?: string;
    openQuickQa?: boolean;
    switchChatEvaluate?: boolean;
  }>({}); // 机器人设置状态

  // ==================== 智能压缩器实例 ====================
  const smartCompressor = useMemo(
    () =>
      new SmartCompressor({
        enableProgressLog: true, // 启用详细日志
        forceMethod: "parameter", // 🔧 强制压缩方法: 'auto'(智能) | 'ffmpeg' | 'image' | 'parameter' | 'webcodecs' | 'canvas'
      }),
    []
  );

  // ==================== 获取快捷问题 ====================
  const fetchQuickQuestions = useCallback(async () => {
    try {
      const response = await chatApi.fetchQuickQuestion(code);
      if (response.success && response.data) {
        setQuickQuestions(response.data);
        console.log("✅ 快捷问题获取成功:", response.data);
      } else {
        console.warn("⚠️ 快捷问题获取失败:", response);
      }
    } catch (error) {
      console.error("❌ 快捷问题获取异常:", error);
    }
  }, [code]);

  // ==================== 获取机器人设置 ====================
  const fetchRobotSettings = useCallback(async () => {
    try {
      const response = await fetchSetting(code);
      if (response.success && response.data) {
        setRobotSettings(response.data);
        console.log("✅ 机器人设置获取成功:", response.data);
      } else {
        console.warn("⚠️ 机器人设置获取失败:", response);
      }
    } catch (error) {
      console.error("❌ 机器人设置获取异常:", error);
    }
  }, [code]);

  // 初始化时获取快捷问题和设置
  useEffect(() => {
    if (code) {
      fetchQuickQuestions();
      fetchRobotSettings();
    }
  }, [fetchQuickQuestions, fetchRobotSettings, code]);

  // ==================== 基础函数 ====================
  // 清除图片和视频状态
  const clearSelectedMedia = useCallback(() => {
    setPreviewBase64(null);
    setUploadedImageUrl(null);
    uploadedImageUrlRef.current = null;
    setPreviewVideo(null);
    setUploadedVideoUrl(null);
    uploadedVideoUrlRef.current = null;
  }, []);

  // 触发文件选择器
  const triggerFileSelector = useCallback(() => {
    if (fileInputRef.current) {
      try {
        fileInputRef.current.click();
      } catch (error) {
        console.error("❌ 文件选择器触发失败:", error);
      }
    }
  }, []);

  // 处理文件选择
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0]; // 暂时只处理第一个文件

      // 检查文件类型
      if (file.type.startsWith("image/")) {
        // 处理图片文件 - 直接调用图片上传逻辑
        try {
          setImageLoading(true);

          // 记录原始文件信息
          const originalSizeMB = file.size / 1024 / 1024;
          console.log("🖼️ 开始处理图片文件...");
          console.log(`📊 原始图片信息:`, {
            文件名: file.name,
            格式: file.type,
            大小: `${originalSizeMB.toFixed(2)}MB`,
            "大小(字节)": file.size.toLocaleString(),
          });

          // 先生成预览（使用原始文件）
          const base64 = await getBase64(file);
          setPreviewBase64(base64);

          // 使用SmartCompressor进行智能压缩
          console.log("🧠 开始智能压缩处理...");
          const compressionResult = await smartCompressor.compress(file);

          // 将blob转换为File对象
          let finalFile = new File([compressionResult.blob], file.name, {
            type: compressionResult.blob.type,
            lastModified: Date.now(),
          });
          const finalSizeMB = finalFile.size / 1024 / 1024;

          // 详细记录压缩结果
          console.log("✅ 智能压缩处理完成:");
          console.log(`📈 处理结果:`, {
            成功: compressionResult.success,
            策略: compressionResult.strategy,
            方法: compressionResult.method,
            质量等级: compressionResult.qualityLevel,
            原始大小: `${(compressionResult.originalSize / 1024 / 1024).toFixed(
              2
            )}MB`,
            处理后大小: `${(
              compressionResult.compressedSize /
              1024 /
              1024
            ).toFixed(2)}MB`,
            压缩率: `${((1 - compressionResult.compressionRatio) * 100).toFixed(
              1
            )}%`,
            节省空间: `${(
              (compressionResult.originalSize -
                compressionResult.compressedSize) /
              1024 /
              1024
            ).toFixed(2)}MB`,
            处理时间: `${(compressionResult.processingTime / 1000).toFixed(
              2
            )}秒`,
            最终文件名: finalFile.name,
            最终格式: finalFile.type,
          });

          // 压缩效果提示
          if (compressionResult.success) {
            const savedMB =
              (compressionResult.originalSize -
                compressionResult.compressedSize) /
              1024 /
              1024;
            if (savedMB > 0) {
              console.log(
                `🎯 压缩效果: 原始 ${originalSizeMB.toFixed(
                  1
                )}MB → 压缩后 ${finalSizeMB.toFixed(
                  1
                )}MB，节省 ${savedMB.toFixed(1)}MB 空间`
              );
            } else {
              console.log(
                `📝 已应用${compressionResult.strategy}策略，无需压缩或已优化`
              );
            }
          } else {
            console.log("⚠️ 压缩处理失败，使用原始文件");
            finalFile = file;
          }

          // 检查最终文件大小
          if (finalSizeMB > 50) {
            console.error(
              `❌ 图片过大: ${finalSizeMB.toFixed(1)}MB > 50MB 限制`
            );
            antMessage.error(
              intl.formatMessage({ id: "file.image.size.error" })
            );
            setPreviewBase64(null);
            return;
          }

          // 创建FormData对象
          const formData = new FormData();
          formData.append("file", finalFile);
          formData.append("token", tokenRef.current);

          // 上传图片
          console.log("🚀 开始上传图片到服务器...");
          const response = await chatApi.uploadImage(formData);
          if (response.success && response.data) {
            console.log("✅ 图片上传成功!", {
              上传地址: response.data,
              最终处理文件: finalFile.name,
              最终大小: `${finalSizeMB.toFixed(2)}MB`,
            });
            // 设置实际图片URL
            setUploadedImageUrl(response.data);
            uploadedImageUrlRef.current = response.data;
            // 设置一个空格到输入框
            setContent(" ");
          } else {
            console.error("❌ 图片上传失败:", response);
            antMessage.error(intl.formatMessage({ id: "image.upload.error" }));
            setPreviewBase64(null);
          }
        } catch (error) {
          console.error("❌ 图片处理失败:", {
            错误信息: error instanceof Error ? error.message : "未知错误",
            文件名: file.name,
            文件大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
            错误详情: error,
          });
          antMessage.error(intl.formatMessage({ id: "image.process.error" }));
          setPreviewBase64(null);
        } finally {
          setImageLoading(false);
          console.log("🏁 图片处理流程结束");
        }
      } else if (file.type.startsWith("video/")) {
        // 处理视频文件 - 直接调用视频上传逻辑
        try {
          setVideoLoading(true);

          // 记录原始文件信息
          const originalSizeMB = file.size / 1024 / 1024;
          console.log("📹 开始处理视频文件...");
          console.log(`📊 原始视频信息:`, {
            文件名: file.name,
            格式: file.type,
            大小: `${originalSizeMB.toFixed(2)}MB`,
            "大小(字节)": file.size.toLocaleString(),
          });

          // 统一按比例压缩所有视频（不再跳过任何文件）
          console.log("🔧 开始统一比例压缩处理...");

          // 智能压缩视频
          const compressionResult = await smartCompressor.compress(file);

          // 将blob转换为File对象
          let finalFile = new File([compressionResult.blob], file.name, {
            type: compressionResult.blob.type,
            lastModified: Date.now(),
          });
          const finalSizeMB = finalFile.size / 1024 / 1024;

          // 详细记录压缩结果
          console.log("✅ 智能视频压缩完成:");
          console.log(`📈 处理结果:`, {
            成功: compressionResult.success,
            策略: compressionResult.strategy,
            方法: compressionResult.method,
            质量等级: compressionResult.qualityLevel,
            原始大小: `${(compressionResult.originalSize / 1024 / 1024).toFixed(
              2
            )}MB`,
            处理后大小: `${(
              compressionResult.compressedSize /
              1024 /
              1024
            ).toFixed(2)}MB`,
            压缩率: `${((1 - compressionResult.compressionRatio) * 100).toFixed(
              1
            )}%`,
            节省空间: `${(
              (compressionResult.originalSize -
                compressionResult.compressedSize) /
              1024 /
              1024
            ).toFixed(2)}MB`,
            处理时间: `${(compressionResult.processingTime / 1000).toFixed(
              2
            )}秒`,
            最终文件名: finalFile.name,
            最终格式: finalFile.type,
          });

          // 压缩效果提示
          if (compressionResult.success) {
            const savedMB =
              (compressionResult.originalSize -
                compressionResult.compressedSize) /
              1024 /
              1024;
            if (savedMB > 0) {
              console.log(
                `🎯 压缩效果: 原始 ${originalSizeMB.toFixed(
                  1
                )}MB → 压缩后 ${finalSizeMB.toFixed(
                  1
                )}MB，节省 ${savedMB.toFixed(1)}MB 空间`
              );
              console.log(
                `📊 使用${compressionResult.strategy}策略，${compressionResult.method}方法`
              );
            } else {
              console.log(
                `📝 已应用${compressionResult.strategy}策略，无需压缩或已优化`
              );
            }
          } else {
            console.log("⚠️ 压缩处理失败，使用原始文件");
            finalFile = file;
          }

          // 检查最终文件大小（降低限制减少服务器压力）
          if (finalSizeMB > 50) {
            console.error(
              `❌ 文件过大: ${finalSizeMB.toFixed(1)}MB > 50MB 限制`
            );
            antMessage.error(
              intl.formatMessage({ id: "file.video.size.error" })
            );
            return;
          }

          // 生成视频缩略图（使用原始文件生成更好的质量）
          const thumbnail = await new Promise<string>((resolve, reject) => {
            const video = document.createElement("video");
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            video.onloadedmetadata = () => {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              video.currentTime = 1; // 获取第1秒的帧
            };

            video.onseeked = () => {
              if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.8));
              } else {
                reject(
                  new Error(
                    intl.formatMessage({ id: "video.thumbnail.generate.error" })
                  )
                );
              }
            };

            video.onerror = () =>
              reject(new Error(intl.formatMessage({ id: "video.load.error" })));
            video.src = URL.createObjectURL(file);
          });
          setPreviewVideo(thumbnail);

          // 创建FormData对象
          const formData = new FormData();
          formData.append("file", finalFile);
          formData.append("token", tokenRef.current);

          // 上传视频
          console.log("🚀 开始上传视频到服务器...");
          const response = await commonApi.uploadVideo(formData);
          if (response.success && response.data) {
            console.log("✅ 视频上传成功!", {
              上传地址: response.data,
              最终处理文件: finalFile.name,
              最终大小: `${finalSizeMB.toFixed(2)}MB`,
            });
            // 设置实际视频URL
            setUploadedVideoUrl(response.data);
            uploadedVideoUrlRef.current = response.data;
            // 设置一个空格到输入框
            setContent(" ");
          } else {
            console.error("❌ 视频上传失败:", response);
            antMessage.error(intl.formatMessage({ id: "video.upload.error" }));
            setPreviewVideo(null);
          }
        } catch (error) {
          console.error("❌ 视频处理失败:", {
            错误信息: error instanceof Error ? error.message : "未知错误",
            文件名: file.name,
            文件大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
            错误详情: error,
          });
          antMessage.error(intl.formatMessage({ id: "video.process.error" }));
          setPreviewVideo(null);
        } finally {
          setVideoLoading(false);
          console.log("🏁 视频处理流程结束");
        }
      } else {
        antMessage.error(intl.formatMessage({ id: "file.type.error" }));
      }

      // 清空input值，允许重复选择同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [intl, smartCompressor, tokenRef]
  );

  // 评分处理函数
  const handleRateMessage = useCallback(
    async (uuid: string, rating: number) => {
      try {
        const currentRating = messageRatings[uuid];

        // 如果点击的是当前已选中的评分，则取消评分
        if (currentRating === rating) {
          const response = await chatApi.rateMemory({
            uuid: uuid,
            rating: 0, // 0表示取消评分
          });

          if (response.success) {
            // 更新评分状态为未评分
            setMessageRatings((prev) => {
              const newRatings = { ...prev };
              delete newRatings[uuid];
              return newRatings;
            });
          }
          return;
        }

        // 如果点击的是不同的评分，则切换评分
        const response = await chatApi.rateMemory({
          uuid: uuid,
          rating: rating,
        });

        if (response.success) {
          // 更新评分状态
          setMessageRatings((prev) => ({
            ...prev,
            [uuid]: rating,
          }));
        }
      } catch (error) {
        console.error("Error rating message:", error);
      }
    },
    [messageRatings]
  );

  // 转换文件为Base64
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // 预加载图片
  const preloadImage = useCallback((url: string) => {
    const img = new Image();
    img.src = url;
  }, []);

  // 生成视频缩略图
  const generateVideoThumbnail = useCallback(
    (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        video.onloadedmetadata = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          video.currentTime = 1; // 获取第1秒的帧
        };

        video.onseeked = () => {
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          } else {
            reject(
              new Error(
                intl.formatMessage({ id: "video.thumbnail.generate.error" })
              )
            );
          }
        };

        video.onerror = () =>
          reject(new Error(intl.formatMessage({ id: "video.load.error" })));
        video.src = URL.createObjectURL(file);
      });
    },
    [intl]
  );

  // ==================== 创建QA记录 ====================
  const createQaRecord = async (
    message: string | null,
    imageUrl: string | null,
    videoUrl: string | null
  ) => {
    try {
      const chatQA = {
        token: tokenRef.current,
        content: message || "",
        imageUrl: imageUrl,
        videoUrl: videoUrl,
      };

      const createResponse = await chatApi.createMemory(chatQA);

      // 检查是否返回C_1错误码（聊天已关闭）
      if (
        createResponse &&
        createResponse.success === false &&
        createResponse.code === "C_1"
      ) {
        setChatStatus("closed-no-eval");
        return null;
      }

      // 创建成功后，添加消息到聊天记录
      if (createResponse.success && createResponse.data) {
        // todo:图片和内容是两条记录，所以需要分别添加图片和文字消息
        const list = createResponse.data;
        // 分别添加图片和文字消息
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          // 如果有图片，添加图片消息
          if (imageUrl) {
            // 获取返回的图片记忆
            const imageMemory = list.find(
              (item: any) => item.contentType === "IMAGE"
            );
            newMessages.push({
              key: `${imageMemory.uuid}`,
              role: "local",
              content: imageUrl,
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(imageMemory.createTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                </Flex>
              ),
            });
          }

          // 如果有文字，添加文字消息
          if (message) {
            // 获取返回的文字记忆
            const textMemory = list.find(
              (item: any) => item.contentType === "TEXT"
            );
            newMessages.push({
              key: `${textMemory.uuid}`,
              role: "local",
              content: message,
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(textMemory.createTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                </Flex>
              ),
            });
          }

          // 如果有视频，添加视频消息
          if (videoUrl) {
            const videoMemory = list.find(
              (item: any) => item.contentType === "VIDEO"
            );
            newMessages.push({
              key: `${videoMemory.uuid}`,
              role: "local",
              content: videoUrl,
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(videoMemory.createTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                </Flex>
              ),
            });
          }

          return newMessages;
        });

        return createResponse.data;
      }
      antMessage.error("Failed to create conversation!");
      return null;
    } catch (error) {
      console.error("Error creating QA record:", error);
      antMessage.error("Failed to create conversation!");
      return null;
    }
  };

  // ==================== 图片处理函数 ====================
  // 图片上传处理（带压缩功能）
  const handleImageUpload = useCallback(
    async (file: RcFile) => {
      if (!file.type.startsWith("image/")) {
        antMessage.error(intl.formatMessage({ id: "file.image.type.error" }));
        return false;
      }

      try {
        setImageLoading(true);

        // 记录原始文件信息
        const originalSizeMB = file.size / 1024 / 1024;
        console.log("🖼️ 开始处理图片文件...");
        console.log(`📊 原始图片信息:`, {
          文件名: file.name,
          格式: file.type,
          大小: `${originalSizeMB.toFixed(2)}MB`,
          "大小(字节)": file.size.toLocaleString(),
        });

        // 先生成预览（使用原始文件）
        const base64 = await getBase64(file);
        setPreviewBase64(base64);

        // 使用SmartCompressor进行智能压缩
        console.log("🧠 开始智能压缩处理...");
        const compressionResult = await smartCompressor.compress(file);

        // 将blob转换为File对象
        let finalFile = new File([compressionResult.blob], file.name, {
          type: compressionResult.blob.type,
          lastModified: Date.now(),
        });
        const finalSizeMB = finalFile.size / 1024 / 1024;

        // 详细记录压缩结果
        console.log("✅ 智能压缩处理完成:");
        console.log(`📈 处理结果:`, {
          成功: compressionResult.success,
          策略: compressionResult.strategy,
          方法: compressionResult.method,
          质量等级: compressionResult.qualityLevel,
          原始大小: `${(compressionResult.originalSize / 1024 / 1024).toFixed(
            2
          )}MB`,
          处理后大小: `${(
            compressionResult.compressedSize /
            1024 /
            1024
          ).toFixed(2)}MB`,
          压缩率: `${((1 - compressionResult.compressionRatio) * 100).toFixed(
            1
          )}%`,
          节省空间: `${(
            (compressionResult.originalSize -
              compressionResult.compressedSize) /
            1024 /
            1024
          ).toFixed(2)}MB`,
          处理时间: `${(compressionResult.processingTime / 1000).toFixed(2)}秒`,
          最终文件名: finalFile.name,
          最终格式: finalFile.type,
        });

        // 压缩效果提示
        if (compressionResult.success) {
          const savedMB =
            (compressionResult.originalSize -
              compressionResult.compressedSize) /
            1024 /
            1024;
          if (savedMB > 0) {
            console.log(
              `🎯 压缩效果: 原始 ${originalSizeMB.toFixed(
                1
              )}MB → 压缩后 ${finalSizeMB.toFixed(1)}MB，节省 ${savedMB.toFixed(
                1
              )}MB 空间`
            );
          } else {
            console.log(
              `📝 已应用${compressionResult.strategy}策略，无需压缩或已优化`
            );
          }
        } else {
          console.log("⚠️ 压缩处理失败，使用原始文件");
          finalFile = file;
        }

        // 检查最终文件大小
        if (finalSizeMB > 50) {
          console.error(`❌ 图片过大: ${finalSizeMB.toFixed(1)}MB > 50MB 限制`);
          antMessage.error(intl.formatMessage({ id: "file.image.size.error" }));
          setPreviewBase64(null);
          return false;
        }

        // 创建FormData对象
        const formData = new FormData();
        formData.append("file", finalFile);
        formData.append("token", tokenRef.current);

        // 上传图片
        console.log("🚀 开始上传图片到服务器...");
        const response = await chatApi.uploadImage(formData);
        if (response.success && response.data) {
          console.log("✅ 图片上传成功!", {
            上传地址: response.data,
            最终处理文件: finalFile.name,
            最终大小: `${finalSizeMB.toFixed(2)}MB`,
          });
          // 设置实际图片URL
          setUploadedImageUrl(response.data);
          uploadedImageUrlRef.current = response.data;
          // 设置一个空格到输入框
          setContent(" ");
          return false;
        } else {
          console.error("❌ 图片上传失败:", response);
          antMessage.error(intl.formatMessage({ id: "image.upload.error" }));
          setPreviewBase64(null);
          return false;
        }
      } catch (error) {
        console.error("❌ 图片处理失败:", {
          错误信息: error instanceof Error ? error.message : "未知错误",
          文件名: file.name,
          文件大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
          错误详情: error,
        });
        antMessage.error(intl.formatMessage({ id: "image.process.error" }));
        setPreviewBase64(null);
        return false;
      } finally {
        setImageLoading(false);
        console.log("🏁 图片处理流程结束");
      }
    },
    [tokenRef, smartCompressor, intl]
  );

  // 视频上传处理（静默压缩版本）
  const handleVideoUpload = useCallback(
    async (file: RcFile) => {
      if (!file.type.startsWith("video/")) {
        antMessage.error(intl.formatMessage({ id: "file.video.type.error" }));
        return false;
      }

      try {
        setVideoLoading(true);

        // 记录原始文件信息
        const originalSizeMB = file.size / 1024 / 1024;
        console.log("📹 开始处理视频文件...");
        console.log(`📊 原始视频信息:`, {
          文件名: file.name,
          格式: file.type,
          大小: `${originalSizeMB.toFixed(2)}MB`,
          "大小(字节)": file.size.toLocaleString(),
        });

        // 统一按比例压缩所有视频（不再跳过任何文件）
        // console.log('🔧 开始统一比例压缩处理...');

        // 智能压缩视频
        // const compressionResult = await smartCompressor.compress(file);

        // // 将blob转换为File对象
        // let finalFile = new File([compressionResult.blob], file.name, {
        //   type: compressionResult.blob.type,
        //   lastModified: Date.now()
        // });

        // // 详细记录压缩结果
        // console.log('✅ 智能视频压缩完成:');
        // console.log(`📈 处理结果:`, {
        //   成功: compressionResult.success,
        //   策略: compressionResult.strategy,
        //   方法: compressionResult.method,
        //   质量等级: compressionResult.qualityLevel,
        //   原始大小: `${(compressionResult.originalSize / 1024 / 1024).toFixed(2)}MB`,
        //   处理后大小: `${(compressionResult.compressedSize / 1024 / 1024).toFixed(2)}MB`,
        //   压缩率: `${((1 - compressionResult.compressionRatio) * 100).toFixed(1)}%`,
        //   节省空间: `${((compressionResult.originalSize - compressionResult.compressedSize) / 1024 / 1024).toFixed(2)}MB`,
        //   处理时间: `${(compressionResult.processingTime / 1000).toFixed(2)}秒`,
        //   最终文件名: finalFile.name,
        //   最终格式: finalFile.type
        // });

        // // 压缩效果提示
        // if (compressionResult.success) {
        //   const savedMB = (compressionResult.originalSize - compressionResult.compressedSize) / 1024 / 1024;
        //   if (savedMB > 0) {
        //     console.log(`🎯 压缩效果: 原始 ${originalSizeMB.toFixed(1)}MB → 压缩后 ${finalSizeMB.toFixed(1)}MB，节省 ${savedMB.toFixed(1)}MB 空间`);
        //     console.log(`📊 使用${compressionResult.strategy}策略，${compressionResult.method}方法`);
        //   } else {
        //     console.log(`📝 已应用${compressionResult.strategy}策略，无需压缩或已优化`);
        //   }
        // } else {
        //   console.log('⚠️ 压缩处理失败，使用原始文件');
        //   finalFile = file;
        // }

        // 检查最终文件大小（降低限制减少服务器压力）
        if (originalSizeMB > 100) {
          console.error(
            `❌ 文件过大: ${originalSizeMB.toFixed(1)}MB > 100MB 限制`
          );
          antMessage.error(intl.formatMessage({ id: "file.video.size.error" }));
          return false;
        }

        // 生成视频缩略图（使用原始文件生成更好的质量）
        const thumbnail = await generateVideoThumbnail(file);
        setPreviewVideo(thumbnail);

        // 创建FormData对象
        const formData = new FormData();
        formData.append("file", file);
        formData.append("token", tokenRef.current);

        // 上传视频
        console.log("🚀 开始上传视频到服务器...");
        const response = await commonApi.uploadVideo(formData);
        if (response.success && response.data) {
          // 设置实际视频URL
          setUploadedVideoUrl(response.data);
          uploadedVideoUrlRef.current = response.data;
          // 设置一个空格到输入框
          setContent(" ");
          return false;
        } else {
          console.error("❌ 视频上传失败:", response);
          antMessage.error(intl.formatMessage({ id: "video.upload.error" }));
          setPreviewVideo(null);
          return false;
        }
      } catch (error) {
        console.error("❌ 视频处理失败:", {
          错误信息: error instanceof Error ? error.message : "未知错误",
          文件名: file.name,
          文件大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
          错误详情: error,
        });
        antMessage.error(intl.formatMessage({ id: "video.process.error" }));
        setPreviewVideo(null);
        return false;
      } finally {
        setVideoLoading(false);
        console.log("🏁 视频处理流程结束");
      }
    },
    [tokenRef, generateVideoThumbnail, intl]
  );

  // 优化预览处理
  const handlePreview = useCallback(
    (image: string) => {
      preloadImage(image);
      setPreviewImage(image);
      setPreviewVisible(true);
    },
    [preloadImage]
  );

  // 优化取消预览
  const handleCancel = useCallback(() => {
    setPreviewVisible(false);
  }, []);

  // 视频预览处理
  const handleVideoPreview = useCallback((videoUrl: string) => {
    setCurrentVideo(videoUrl);
    setVideoVisible(true);
  }, []);

  // 取消视频预览
  const handleVideoCancel = useCallback(() => {
    setVideoVisible(false);
  }, []);

  // ==================== 关闭聊天功能 ====================
  const handleCloseChat = useCallback(async () => {
    try {
      // 调用后端接口关闭聊天会话
      const response = await chatApi.closeChat({
        token: tokenRef.current,
      });

      if (response.success) {
        // 后端关闭成功，清理前端状态
        clearSelectedMedia();
        setContent("");

        // 根据设置决定状态和是否插入评价消息
        if (robotSettings.switchChatEvaluate) {
          setChatStatus("closed-need-eval");
          fetchChatContent();
        } else {
          setChatStatus("closed-no-eval");
        }

        // 显示成功消息
        antMessage.success(intl.formatMessage({ id: "close.chat.success" }));
      } else {
        antMessage.error(intl.formatMessage({ id: "close.chat.error" }));
      }
    } catch (error) {
      antMessage.error(intl.formatMessage({ id: "close.chat.error" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSelectedMedia, intl, robotSettings.switchChatEvaluate]);

  // ==================== 获取评分描述 ====================
  const getRatingDesc = useCallback(
    (value: number) => {
      const descriptions = [
        "", // 0星
        intl.formatMessage(
          { id: "evaluation.rating.1" },
          { defaultMessage: "非常不满意" }
        ),
        intl.formatMessage(
          { id: "evaluation.rating.2" },
          { defaultMessage: "不满意" }
        ),
        intl.formatMessage(
          { id: "evaluation.rating.3" },
          { defaultMessage: "一般" }
        ),
        intl.formatMessage(
          { id: "evaluation.rating.4" },
          { defaultMessage: "满意" }
        ),
        intl.formatMessage(
          { id: "evaluation.rating.5" },
          { defaultMessage: "非常满意" }
        ),
      ];
      return descriptions[value] || "";
    },
    [intl]
  );

  // ==================== 处理评价提交 ====================
  const handleEvaluationSubmit = useCallback(
    async (rating: number, feedback?: string) => {
      try {
        const response = await chatApi.submitEvaluation({
          token: tokenRef.current,
          rating,
          feedback,
        });

        if (response.success) {
          setChatStatus("closed-evaluated");
          // 更新评价组件消息为已完成状态，显示用户的评价内容
          setMessages((prev) =>
            prev.map((msg) =>
              typeof msg.key === "string" &&
              msg.key.startsWith("evaluation-component-")
                ? {
                    ...msg,
                    content: (
                      <div
                        style={{
                          padding: "20px",
                          borderRadius: "12px",
                          border: "1px solid #d9f7be",
                          background:
                            "linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)",
                        }}
                      >
                        {/* 标题 */}
                        <div
                          style={{
                            textAlign: "center",
                            marginBottom: "16px",
                            color: "#52c41a",
                            fontSize: "16px",
                            fontWeight: 600,
                          }}
                        >
                          <CheckOutlined style={{ marginRight: "8px" }} />
                          {intl.formatMessage(
                            { id: "evaluation.completed" },
                            { defaultMessage: "评价已完成" }
                          )}
                        </div>

                        {/* 评分显示 */}
                        <div
                          style={{ textAlign: "center", marginBottom: "16px" }}
                        >
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#666",
                              marginBottom: "8px",
                            }}
                          >
                            {intl.formatMessage(
                              { id: "evaluation.your.rating" },
                              { defaultMessage: "您的评分" }
                            )}
                            :
                          </div>
                          <div>
                            {[...Array(5)].map((_, index) => (
                              <span
                                key={index}
                                style={{
                                  fontSize: "20px",
                                  color: index < rating ? "#faad14" : "#f0f0f0",
                                  marginRight: "4px",
                                }}
                              >
                                ⭐
                              </span>
                            ))}
                            <span
                              style={{
                                marginLeft: "8px",
                                color: "#1890ff",
                                fontWeight: 500,
                              }}
                            >
                              ({rating}/5 - {getRatingDesc(rating)})
                            </span>
                          </div>
                        </div>

                        {/* 文字反馈显示 */}
                        {feedback && (
                          <div
                            style={{
                              background: "rgba(255, 255, 255, 0.8)",
                              padding: "12px",
                              borderRadius: "8px",
                              border: "1px solid #e8f4fd",
                              marginBottom: "12px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#666",
                                marginBottom: "8px",
                              }}
                            >
                              {intl.formatMessage(
                                { id: "evaluation.your.feedback" },
                                { defaultMessage: "您的反馈" }
                              )}
                              :
                            </div>
                            <div
                              style={{
                                color: "#262626",
                                lineHeight: "1.5",
                                fontSize: "14px",
                              }}
                            >
                              "{feedback}"
                            </div>
                          </div>
                        )}

                        {/* 感谢信息 */}
                        <div
                          style={{
                            textAlign: "center",
                            color: "#8c8c8c",
                            fontSize: "12px",
                          }}
                        >
                          {intl.formatMessage(
                            { id: "evaluation.thank.you" },
                            {
                              defaultMessage:
                                "感谢您的宝贵反馈，我们会持续改进服务！",
                            }
                          )}
                        </div>
                      </div>
                    ),
                  }
                : msg
            )
          );
          antMessage.success(
            intl.formatMessage(
              { id: "evaluation.submit.success" },
              { defaultMessage: "评价提交成功" }
            )
          );
        } else {
          antMessage.error(
            intl.formatMessage(
              { id: "evaluation.submit.error" },
              { defaultMessage: "评价提交失败" }
            )
          );
        }
      } catch (error) {
        console.error("评价提交失败:", error);
        antMessage.error(
          intl.formatMessage(
            { id: "evaluation.submit.error" },
            { defaultMessage: "评价提交失败" }
          )
        );
      }
    },
    [tokenRef, intl, getRatingDesc]
  );

  // ==================== 处理跳过评价 ====================
  const handleEvaluationSkip = useCallback(() => {
    setChatStatus("closed-evaluated");
    // 更新评价组件消息为跳过状态
    setMessages((prev) =>
      prev.map((msg) =>
        typeof msg.key === "string" &&
        msg.key.startsWith("evaluation-component-")
          ? {
              ...msg,
              content: (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#8c8c8c",
                    background: "#fafafa",
                    borderRadius: "8px",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  {intl.formatMessage(
                    { id: "evaluation.skipped" },
                    { defaultMessage: "已跳过评价" }
                  )}
                </div>
              ),
            }
          : msg
      )
    );
  }, [intl]);

  // ==================== 处理快捷问题点击 ====================
  const handleQuickQuestionClick = useCallback(
    async (question: Bot.QuickQuestion) => {
      try {
        console.log("快捷问题点击:", question);

        // 调用快捷回复接口
        const response = await chatApi.quickAnswer({
          token: tokenRef.current,
          quickQaId: question.id,
        });

        if (response.success && response.data) {
          console.log("快捷回复成功，返回数据:", response.data);

          // 将返回的数据转换为消息格式
          const newMessages = response.data.map((item: any) => {
            const message: ChatMessage = {
              key: `${item.uuid}`,
              role: item.type === "USER" ? "local" : "ai",
              content: item.content,
              typing: false,
              uuid: item.uuid,
              rating: messageRatings[item.uuid],
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(item.createTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                  {item.type !== "USER" && (
                    <RatingButtons
                      uuid={item.uuid}
                      rating={messageRatings[item.uuid]}
                      onRate={handleRateMessage}
                    />
                  )}
                </Flex>
              ),
            };
            return message;
          });

          // 添加新消息到聊天记录（避免重复）
          setMessages((prevMessages) => {
            const existingKeys = new Set(prevMessages.map((msg) => msg.key));
            const uniqueNewMessages = newMessages.filter(
              (msg: any) => !existingKeys.has(msg.key)
            );
            return [...prevMessages, ...uniqueNewMessages];
          });

          // 显示成功提示
          // antMessage.success(intl.formatMessage({ id: 'quick.question.success' }));
        } else {
          console.error("快捷回复失败:", response);
          // antMessage.error(intl.formatMessage({ id: 'quick.question.send.error' }));
        }
      } catch (error) {
        console.error("快捷回复异常:", error);
        // antMessage.error(intl.formatMessage({ id: 'quick.question.error' }));
      }
    },
    [tokenRef, messageRatings, handleRateMessage]
  );

  // ==================== 获取聊天内容 ====================
  const fetchChatContent = useCallback(async () => {
    if (!tokenRef.current) return;
    const combinedMessages = [];

    try {
      const response = await chatApi.listMemory({
        token: tokenRef.current,
        pageSize: 30,
        current: 1,
      });

      // 检查是否返回C_1错误码（聊天已关闭）
      if (response && response.success === false && response.code === "C_1") {
        setChatStatus("closed-no-eval");
        return;
      }

      if (response.success && response.data && response.data.length > 0) {
        const localMessages = response.data.flatMap((item: any) => {
          const messages: ChatMessage[] = [];
          if (item.type === "USER" && item.content) {
            messages.push({
              key: `${item.uuid}`,
              role: "local",
              content: item.content,
              typing: false,
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(item.createTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                </Flex>
              ),
            });
          } else if (
            (item.type === "BOT" || item.type === "HUMAN") &&
            item.content
          ) {
            messages.push({
              key: `${item.uuid}`,
              role: "ai",
              content: item.content,
              typing: false,
              uuid: item.uuid,
              rating: messageRatings[item.uuid],
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(item.modifyTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                  <RatingButtons
                    uuid={item.uuid}
                    rating={messageRatings[item.uuid]}
                    onRate={handleRateMessage}
                  />
                </Flex>
              ),
            });
          } else if (item.type === "PROLOGUE" && item.content) {
            messages.push({
              key: `${item.uuid}`,
              role: "ai",
              content: item.content,
              typing: false,
              footer: (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {new Date(item.modifyTime * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                </Flex>
              ),
            });
          } else if (
            item.type === "EVALUATION" &&
            item.contentType === "TEXT"
          ) {
            // 处理评价消息 - 插入两条消息：提示消息和评价组件
            messages.push(
              // 第一条：评价提示消息
              {
                key: `evaluation-prompt-${item.uuid}`,
                role: "ai",
                content: intl.formatMessage(
                  { id: "evaluation.prompt.message" },
                  {
                    defaultMessage:
                      "感谢您的使用！请为本次对话体验进行评价，您的反馈对我们很重要。",
                  }
                ),
                typing: false,
                footer: (
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ width: "100%" }}
                  >
                    <Space size={8}>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: "12px" }}
                      >
                        {new Date(item.modifyTime * 1000).toLocaleString(
                          "zh-CN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          }
                        )}
                      </Typography.Text>
                    </Space>
                  </Flex>
                ),
              },
              // 第二条：评价组件
              {
                key: `evaluation-component-${item.uuid}`,
                role: "system",
                content: (
                  <ChatEvaluation
                    onSubmit={handleEvaluationSubmit}
                    onSkip={handleEvaluationSkip}
                  />
                ),
                messageType: "evaluation",
              }
            );
          }
          return messages;
        });
        combinedMessages.push(...localMessages);
        setMessages(combinedMessages);
      }

      // 修改消息更新逻辑
      const flattenedMessages = Array.isArray(combinedMessages[0])
        ? combinedMessages.flat()
        : combinedMessages;

      // 使用 Set 来存储已经显示的消息 key
      const messageKeys = new Set();
      const uniqueMessages = flattenedMessages.filter((msg) => {
        if (!msg || !msg.key) return false;
        // 如果消息已经存在，返回 false
        if (messageKeys.has(msg.key)) return false;
        // 否则添加消息 key 并返回 true
        messageKeys.add(msg.key);
        return true;
      });

      setMessages(uniqueMessages);
    } catch (error) {
      console.error("Error fetching chat content:", error);
    }
  }, [
    tokenRef,
    messageRatings,
    handleRateMessage,
    intl,
    handleEvaluationSubmit,
    handleEvaluationSkip,
  ]);

  // ==================== 智能体 ====================
  const [agent] = useXAgent<string>({
    request: async (info, callbacks) => {
      try {
        const imageUrl = uploadedImageUrlRef.current;
        const videoUrl = uploadedVideoUrlRef.current;
        const rawMessage = (info as any)?.message as string | undefined;
        const message =
          rawMessage && rawMessage.trim() !== "" ? rawMessage.trim() : null;

        const result = await createQaRecord(message, imageUrl, videoUrl);
        if (result) {
          callbacks.onSuccess([
            { data: intl.formatMessage({ id: "message.send.success" }) },
          ] as any);
        } else {
          callbacks.onError(
            new Error(intl.formatMessage({ id: "message.send.error" }))
          );
          antMessage.error(intl.formatMessage({ id: "message.send.error" }));
        }
      } catch (error) {
        console.error("Error in request:", error);
        callbacks.onError(error as Error);
        antMessage.error(intl.formatMessage({ id: "message.send.error" }));
      }
    },
  });

  const { onRequest } = useXChat({
    agent,
  });

  // ==================== Fetch Chat Content ====================
  useEffect(() => {
    // 如果聊天已关闭，不启动轮询
    if (chatStatus !== "active") {
      return;
    }

    // 初始加载
    fetchChatContent();

    // 设置定时器，每20秒查询一次
    const timer = setInterval(() => {
      // 如果聊天已关闭，停止轮询
      if (chatStatus !== "active") {
        clearInterval(timer);
        return;
      }
      fetchChatContent();
    }, 10000);

    // 清理定时器
    return () => {
      clearInterval(timer);
    };
  }, [fetchChatContent, chatStatus]);

  // ==================== UI 状态控制 ====================
  // 获取输入框状态
  const getSenderProps = useCallback(() => {
    switch (chatStatus) {
      case "active":
        return {
          disabled: false,
          placeholder: intl.formatMessage(
            { id: "input.placeholder.active" },
            { defaultMessage: "请输入消息..." }
          ),
        };
      case "closed-need-eval":
        return {
          disabled: true,
          placeholder: intl.formatMessage(
            { id: "input.placeholder.need-eval" },
            { defaultMessage: "会话已结束，请完成评价" }
          ),
        };
      case "closed-no-eval":
      case "closed-evaluated":
        return {
          disabled: true,
          placeholder: intl.formatMessage(
            { id: "input.placeholder.closed" },
            { defaultMessage: "会话已结束" }
          ),
        };
      default:
        return { disabled: false, placeholder: "请输入消息..." };
    }
  }, [chatStatus, intl]);

  // 判断是否显示关闭按钮
  const shouldShowCloseButton = chatStatus === "active";

  // 判断是否显示快捷问题
  const shouldShowQuickQuestions = robotSettings.openQuickQa;

  // ==================== Event ====================
  // 优化提交处理
  const onSubmit = useCallback(
    (nextContent: string) => {
      // 如果聊天已关闭，不允许发送
      if (chatStatus !== "active") {
        return;
      }

      // 检查是否有图片、视频或文本内容
      const hasImage = !!uploadedImageUrlRef.current;
      const hasVideo = !!uploadedVideoUrlRef.current;
      const hasText = !!nextContent.trim();

      // 如果既没有文本也没有图片/视频，则不发送
      if (!hasImage && !hasVideo && !hasText) {
        return;
      }

      // 使用onRequest发送消息，当只有媒体文件时传入空格字符串
      onRequest(hasText ? nextContent : " ");

      // 清理状态
      setContent("");
      if (hasImage || hasVideo) {
        clearSelectedMedia();
      }
    },
    [onRequest, clearSelectedMedia, chatStatus]
  );

  const onPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (info) => {
    onRequest(info.data.description as string);
  };

  // ==================== 角色配置 ====================
  const roles: GetProp<typeof Bubble.List, "roles"> = {
    ai: {
      placement: "start",
      typing: { step: 5, interval: 20 },
      styles: {
        content: {
          borderRadius: 16,
        },
      },
      avatar: (
        <img
          src={aiAvatar}
          alt="AI"
          style={{ width: 24, height: 24, borderRadius: "50%" }}
        />
      ),
      messageRender: renderLocalMessage,
    },
    local: {
      placement: "end",
      variant: "shadow",
      avatar: (
        <img
          src={userAvatar}
          alt="User"
          style={{ width: 24, height: 24, borderRadius: "50%" }}
        />
      ),
      messageRender: renderLocalMessage,
    },
    suggestion: {
      placement: "start",
      avatar: { icon: <UserOutlined />, style: { visibility: "hidden" } },
      variant: "borderless",
      messageRender: (items) => (
        <Prompts
          vertical
          items={items as any}
          onItemClick={(info) => {
            onRequest(info.data.description as string);
          }}
        />
      ),
    },
  };

  const items: GetProp<typeof Bubble.List, "items"> = messages;

  // 添加一个ref用于消息容器
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // 添加滚动到底部的函数
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    }, 100); // 100ms 延时确保内容已完全渲染
  }, []);

  // 在消息更新后滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // 暴露 fetchChatContent 方法给父组件
  useEffect(() => {
    if (onRef) {
      onRef({ fetchChatContent });
    }
  }, [onRef, fetchChatContent]);

  // 添加图片预览事件监听
  useEffect(() => {
    const handleImagePreview = (event: CustomEvent) => {
      const { url } = event.detail;
      handlePreview(url);
    };

    window.addEventListener(
      "preview-image",
      handleImagePreview as EventListener
    );

    return () => {
      window.removeEventListener(
        "preview-image",
        handleImagePreview as EventListener
      );
    };
  }, [handlePreview]);

  // 添加视频预览事件监听
  useEffect(() => {
    const handleVideoPreviewEvent = (event: CustomEvent) => {
      const { url } = event.detail;
      handleVideoPreview(url);
    };

    window.addEventListener(
      "preview-video",
      handleVideoPreviewEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "preview-video",
        handleVideoPreviewEvent as EventListener
      );
    };
  }, [handleVideoPreview]);

  // 图片预览组件 - 使用memo优化渲染
  const ImagePreview = useMemo(
    () =>
      React.memo(
        ({
          src,
          onPreview,
          onRemove,
          loading,
        }: {
          src: string;
          onPreview: () => void;
          onRemove: (e: React.MouseEvent) => void;
          loading?: boolean;
        }) => (
          <div className={styles.imagePreview}>
            <img
              src={src}
              alt={intl.formatMessage({ id: "image.preview.alt" })}
              onClick={onPreview}
              style={{ opacity: loading ? 0.6 : 1 }}
            />
            <span className={styles.removeImage} onClick={onRemove}>
              ×
            </span>
            {loading && (
              <div className={styles.imageLoading}>
                <LoadingOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
              </div>
            )}
          </div>
        )
      ),
    [styles.imagePreview, styles.removeImage, styles.imageLoading, intl]
  );

  // 视频预览组件 - 使用memo优化渲染
  const VideoPreview = useMemo(
    () =>
      React.memo(
        ({
          src,
          onPreview,
          onRemove,
          loading,
        }: {
          src: string;
          onPreview: () => void;
          onRemove: (e: React.MouseEvent) => void;
          loading?: boolean;
        }) => (
          <div className={styles.imagePreview}>
            <div
              style={{
                position: "relative",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                opacity: loading ? 0.6 : 1,
              }}
              onClick={onPreview}
            >
              <img
                src={src}
                alt={intl.formatMessage({ id: "video.thumbnail.alt" })}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <PlayCircleOutlined
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "16px",
                  color: "white",
                  textShadow: "0 0 4px rgba(0,0,0,0.5)",
                }}
              />
            </div>
            <span className={styles.removeImage} onClick={onRemove}>
              ×
            </span>
            {loading && (
              <div className={styles.imageLoading}>
                <LoadingOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
              </div>
            )}
          </div>
        )
      ),
    [styles.imagePreview, styles.removeImage, styles.imageLoading, intl]
  );

  return (
    <div className={styles.layout} style={{ position: "relative" }}>
      {/* 🌟 隐藏的文件输入器 - 移到根级别 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={imageLoading || videoLoading}
      />

      <div
        className={styles.chat}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* 添加 ref 到消息列表容器 */}
        <div ref={messagesContainerRef} className={styles.messages}>
          <Bubble.List
            items={items.length > 0 ? items : [{ variant: "borderless" }]}
            roles={roles}
          />
        </div>

        {/* 图片预览区域 */}
        <div
          className={styles.imagePreviewRow}
          style={{
            transform: `scale(${previewBase64 ? 1 : 0})`,
            height: previewBase64 ? "48px" : "0px",
            opacity: previewBase64 ? 1 : 0,
          }}
        >
          {previewBase64 && (
            <ImagePreview
              src={previewBase64}
              onPreview={() => handlePreview(previewBase64)}
              onRemove={(e) => {
                e.stopPropagation();
                clearSelectedMedia();
              }}
              loading={!uploadedImageUrl}
            />
          )}
        </div>

        {/* 视频预览区域 */}
        <div
          className={styles.imagePreviewRow}
          style={{
            transform: `scale(${previewVideo ? 1 : 0})`,
            height: previewVideo ? "48px" : "0px",
            opacity: previewVideo ? 1 : 0,
          }}
        >
          {previewVideo && (
            <VideoPreview
              src={previewVideo}
              onPreview={() => handleVideoPreview(uploadedVideoUrl || "")}
              onRemove={(e) => {
                e.stopPropagation();
                clearSelectedMedia();
              }}
              loading={!uploadedVideoUrl}
            />
          )}
        </div>

        {/* 图片预览模态框 */}
        <Modal
          open={previewVisible}
          footer={null}
          onCancel={handleCancel}
          width={800}
          className={styles.imagePreviewModal}
          centered
        >
          <img alt="preview" src={previewImage} style={{ width: "100%" }} />
        </Modal>

        {/* 视频播放模态框 */}
        <Modal
          open={videoVisible}
          footer={null}
          onCancel={handleVideoCancel}
          width={800}
          className={styles.imagePreviewModal}
          centered
        >
          <video controls style={{ width: "100%" }} src={currentVideo} />
        </Modal>

        {/* 🌟 提示词 */}
        <Prompts items={senderPromptsItems} onItemClick={onPromptsItemClick} />

        {/* 🌟 可拖拽悬浮关闭按钮 */}
        {shouldShowCloseButton && (
          <DraggableCloseButton
            onClose={handleCloseChat}
            disabled={imageLoading || videoLoading}
            containerRef={messagesContainerRef}
          />
        )}

        {/* 🌟 快捷问题浮窗 - 用户点击后立即显示问答 */}
        {shouldShowQuickQuestions && (
          <QuickQuestionFloating
            questions={quickQuestions}
            onQuestionClick={handleQuickQuestionClick}
            containerRef={messagesContainerRef}
            disabled={imageLoading || videoLoading}
            token={tokenRef.current}
            defaultExpanded={robotSettings.openQuickQa || false}
          />
        )}

        <div style={{ width: "100%", padding: "8px 16px" }}>
          {/* 🌟 输入框 */}
          <Sender
            value={content}
            onSubmit={onSubmit}
            onChange={setContent}
            loading={agent.isRequesting() || imageLoading || videoLoading}
            disabled={getSenderProps().disabled || imageLoading || videoLoading}
            placeholder={getSenderProps().placeholder}
            className={styles.sender}
          />
          {/* 🌟 输入框下方的上传工具栏 */}
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%", padding: "4px 2px" }}
          >
            {/* 左：主要上传按钮 - 简洁的"+"按钮 */}
            <div
              onClick={(e) => {
                if (!imageLoading && !videoLoading) {
                  triggerFileSelector();
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#f5f5f5",
                border: "1px solid #d9d9d9",
                cursor:
                  imageLoading || videoLoading ? "not-allowed" : "pointer",
                opacity: imageLoading || videoLoading ? 0.6 : 1,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!imageLoading && !videoLoading) {
                  e.currentTarget.style.backgroundColor = "#e6f7ff";
                  e.currentTarget.style.borderColor = "#1890ff";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
                e.currentTarget.style.borderColor = "#d9d9d9";
              }}
            >
              <PlusOutlined
                style={{
                  fontSize: "14px",
                  color: "#666",
                  fontWeight: "normal",
                }}
              />
            </div>
            {/* 中：快捷上传按钮 - 图片 */}
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleImageUpload}
              disabled={imageLoading || videoLoading}
            >
              <PictureOutlined
                style={{
                  fontSize: "18px",
                  cursor:
                    imageLoading || videoLoading ? "not-allowed" : "pointer",
                  color: imageLoading || videoLoading ? "#d9d9d9" : "#666",
                  transition: "color 0.2s ease",
                }}
              />
            </Upload>
            {/* 右：快捷上传按钮 - 视频 */}
            <Upload
              accept="video/*"
              showUploadList={false}
              beforeUpload={handleVideoUpload}
              disabled={imageLoading || videoLoading}
            >
              <VideoCameraOutlined
                style={{
                  fontSize: "18px",
                  cursor:
                    imageLoading || videoLoading ? "not-allowed" : "pointer",
                  color: imageLoading || videoLoading ? "#d9d9d9" : "#666",
                  transition: "color 0.2s ease",
                }}
              />
            </Upload>
          </Flex>
        </div>
      </div>
    </div>
  );
};

export default IndependentChat;
